
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  LineChart, 
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Calendar,
  Star,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';

const StaffReports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  // Mock data for charts
  const monthlyData = [
    { month: 'Jan', revenue: 45000, patients: 230, treatments: 420 },
    { month: 'Feb', revenue: 52000, patients: 280, treatments: 480 },
    { month: 'Mar', revenue: 48000, patients: 245, treatments: 450 },
    { month: 'Apr', revenue: 61000, patients: 320, treatments: 580 },
    { month: 'May', revenue: 55000, patients: 295, treatments: 520 },
    { month: 'Jun', revenue: 67000, patients: 350, treatments: 620 }
  ];

  const treatmentPopularity = [
    { name: 'Botox', value: 35, color: '#A55C52' },
    { name: 'HydraFacial', value: 25, color: '#CDA79D' },
    { name: 'Lip Filler', value: 20, color: '#F2D9D3' },
    { name: 'Laser Facial', value: 15, color: '#5C322B' },
    { name: 'Other', value: 5, color: '#E8D5D0' }
  ];

  const kpis = [
    {
      title: 'Monthly Revenue',
      value: '$67,000',
      change: '+12%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      title: 'New Patients',
      value: '89',
      change: '+8%',
      trend: 'up',
      icon: Users,
      color: 'text-blue-600'
    },
    {
      title: 'Treatments',
      value: '620',
      change: '+15%',
      trend: 'up',
      icon: Calendar,
      color: 'text-purple-600'
    },
    {
      title: 'Satisfaction',
      value: '4.8',
      change: '+0.2',
      trend: 'up',
      icon: Star,
      color: 'text-juv-terracotta'
    }
  ];

  const topPerformers = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'Medical Director',
      treatments: 95,
      revenue: 28500,
      satisfaction: 4.9
    },
    {
      name: 'Emily Rodriguez',
      role: 'Aesthetic Nurse',
      treatments: 78,
      revenue: 23200,
      satisfaction: 4.8
    },
    {
      name: 'Michael Chen',
      role: 'Nurse Practitioner',
      treatments: 72,
      revenue: 21800,
      satisfaction: 4.7
    }
  ];

  const patientMetrics = [
    { metric: 'New Patient Conversion', value: 85, target: 80 },
    { metric: 'Treatment Retention', value: 92, target: 85 },
    { metric: 'Referral Rate', value: 68, target: 70 },
    { metric: 'Satisfaction Score', value: 96, target: 90 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                Performance Reports
              </h1>
              <p className="mt-1 text-juv-deep-chestnut/70">
                Analytics and insights for your practice
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" className="border-juv-rose-clay text-juv-deep-chestnut">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
              <Button variant="outline" className="border-juv-rose-clay text-juv-deep-chestnut">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button className="bg-juv-terracotta text-white">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Period Selector */}
          <div className="flex space-x-2">
            {['week', 'month', 'quarter', 'year'].map((period) => (
              <Button
                key={period}
                variant={selectedPeriod === period ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod(period)}
                className={selectedPeriod === period ? "bg-juv-terracotta text-white" : "border-juv-rose-clay text-juv-deep-chestnut"}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </Button>
            ))}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kpis.map((kpi, index) => {
              const Icon = kpi.icon;
              return (
                <Card key={index} className="bg-white shadow-luxury border border-juv-rose-clay/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                        <p className="text-2xl font-bold text-juv-deep-chestnut">{kpi.value}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center`}>
                        <Icon className={`h-6 w-6 ${kpi.color}`} />
                      </div>
                    </div>
                    <div className={`flex items-center mt-2 ${kpi.color}`}>
                      <TrendingUp className="h-4 w-4 mr-1" />
                      <span className="text-sm font-medium">{kpi.change}</span>
                      <span className="text-xs text-gray-500 ml-1">vs last {selectedPeriod}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardHeader>
                <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                  Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="revenue" stroke="#A55C52" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Treatment Distribution */}
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardHeader>
                <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                  Treatment Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={treatmentPopularity}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                    >
                      {treatmentPopularity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {treatmentPopularity.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-juv-deep-chestnut">{item.name}</span>
                      </div>
                      <span className="font-medium text-juv-deep-chestnut">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Performers */}
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardHeader>
                <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {topPerformers.map((performer, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-juv-rose-clay/20 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-juv-terracotta text-white flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-juv-deep-chestnut">{performer.name}</p>
                          <p className="text-sm text-gray-600">{performer.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-juv-deep-chestnut">
                          {performer.treatments} treatments
                        </p>
                        <div className="flex items-center">
                          <Star className="h-3 w-3 text-juv-terracotta mr-1" />
                          <span className="text-xs text-gray-600">{performer.satisfaction}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Patient Metrics */}
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardHeader>
                <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                  Patient Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {patientMetrics.map((metric, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-juv-deep-chestnut">
                          {metric.metric}
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            {metric.value}% / {metric.target}%
                          </span>
                          <Badge className={metric.value >= metric.target ? 
                            "bg-green-100 text-green-800" : 
                            "bg-juv-soft-blush text-juv-deep-chestnut"
                          }>
                            {metric.value >= metric.target ? 'Target Met' : 'Below Target'}
                          </Badge>
                        </div>
                      </div>
                      <Progress value={metric.value} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Breakdown */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardHeader>
              <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                Monthly Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#A55C52" />
                  <Bar dataKey="patients" fill="#CDA79D" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StaffReports;
